import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, MapPin, Calendar, DollarSign, ArrowRight, Plus, Send, X, CheckCircle2 } from "lucide-react";
import { OPPORTUNITY_TYPES } from "@/lib/options";

export default function Opportunities() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appliedIds, setAppliedIds] = useState(new Set());
  const [filter, setFilter] = useState("all");
  const [applyOpp, setApplyOpp] = useState(null);   // opportunity being applied to
  const [applyNote, setApplyNote] = useState("");
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyError, setApplyError] = useState("");
  const [applySuccess, setApplySuccess] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/opportunities");
        setOpps(data);
        if (user?.role === "couple") {
          const { data: mine } = await api.get("/opportunities/me/applied");
          setAppliedIds(new Set(mine.map((m) => m.opportunity_id)));
        }
      } catch {} finally { setLoading(false); }
    })();
  }, [user]);

  const openApply = (opp) => {
    setApplyOpp(opp);
    setApplyNote("");
    setApplyError("");
    setApplySuccess(false);
  };

  const closeApply = () => {
    setApplyOpp(null);
    setApplyNote("");
    setApplyError("");
    setApplySuccess(false);
    setApplyBusy(false);
  };

  const submitApply = async () => {
    if (!applyOpp) return;
    setApplyBusy(true);
    setApplyError("");
    try {
      await api.post("/opportunities/apply", { opportunity_id: applyOpp.opp_id, message: applyNote });
      setAppliedIds(new Set([...appliedIds, applyOpp.opp_id]));
      setApplySuccess(true);
    } catch (e) {
      setApplyError(e?.response?.data?.detail || "Couldn't send your application. Try again in a moment.");
    } finally {
      setApplyBusy(false);
    }
  };

  const filtered = filter === "all" ? opps : opps.filter((o) => o.type === filter);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="qld-overline mb-3">opportunities board</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink leading-tight">
              Styled shoots, content days<br/>& editorial gigs.
            </h1>
            <p className="text-muted mt-3 max-w-xl">
              Posted by wedding professionals looking for real lovers. {user?.role === "couple" ? "Apply with one tap." : ""}
            </p>
          </div>
          {user?.role === "vendor" && user?.vendor_status === "approved" && (
            <Link to="/dashboard/vendor/post" className="qld-btn-primary" data-testid="opps-post-cta">
              <Plus className="w-4 h-4" strokeWidth={1.5}/> Post an opportunity
            </Link>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          <button onClick={()=>setFilter("all")} data-testid="opps-filter-all"
            className={`lif-toggle ${filter==="all" ? "lif-toggle-active" : "hover:bg-mustard"}`}>
            All
          </button>
          {OPPORTUNITY_TYPES.map((t) => (
            <button key={t.value} onClick={()=>setFilter(t.value)} data-testid={`opps-filter-${t.value}`}
              className={`lif-toggle ${filter===t.value ? "lif-toggle-active" : "hover:bg-mustard"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-muted">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="qld-card p-12 text-center">
            <Sparkles className="w-8 h-8 mx-auto text-wine mb-3" strokeWidth={1.5}/>
            <h3 className="font-serif text-2xl text-ink mb-2">Nothing here yet.</h3>
            <p className="text-muted">Check back soon — new opportunities post weekly.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="opportunities-grid">
            {filtered.map((o) => {
              const loc = [o.location_city, o.location_state, o.location_country].filter(Boolean).join(", ");
              const typeLabel = OPPORTUNITY_TYPES.find((t)=>t.value===o.type)?.label || o.type;
              const applied = appliedIds.has(o.opp_id);
              return (
                <article key={o.opp_id} className="qld-card p-7 group" data-testid={`opp-card-${o.opp_id}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <span className="qld-chip qld-chip-sky">{typeLabel}</span>
                    {o.event_date && <span className="text-sm text-muted inline-flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" strokeWidth={1.5}/> {o.event_date}
                    </span>}
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight mb-2">{o.title}</h3>
                  <p className="text-sm text-muted mb-4">by {o.vendor_name}</p>
                  <p className="text-ink/85 line-clamp-3 mb-5">{o.description}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-muted mb-5">
                    {loc && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" strokeWidth={1.5}/> {loc}</span>}
                    {o.compensation && <span className="inline-flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" strokeWidth={1.5}/> {o.compensation}</span>}
                  </div>
                  {user?.role === "couple" ? (
                    <button disabled={applied} onClick={()=>openApply(o)}
                      data-testid={`opp-apply-${o.opp_id}`}
                      className={applied ? "lif-btn lif-btn-mustard w-full opacity-70 cursor-default" : "qld-btn-primary w-full"}>
                      {applied ? <>Applied <Sparkles className="w-4 h-4" strokeWidth={1.5}/></>
                              : <>Apply <Send className="w-4 h-4" strokeWidth={1.5}/></>}
                    </button>
                  ) : !user ? (
                    <button onClick={()=>navigate("/join?role=couple")} className="qld-btn-primary w-full">
                      Sign up as a lover to apply <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
                    </button>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* Apply modal — on-brand brutalist sheet */}
      {applyOpp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8 overflow-y-auto"
          onClick={closeApply}
          data-testid="apply-modal"
        >
          <div
            className="bg-linen border-2 border-black rounded-[1.5rem] w-full max-w-lg p-7 md:p-9 relative animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeApply}
              className="absolute top-4 right-4 w-9 h-9 rounded-full border-2 border-black bg-linen hover:bg-wine hover:text-white transition flex items-center justify-center"
              aria-label="Close"
              data-testid="apply-modal-close"
            >
              <X className="w-4 h-4" strokeWidth={1.5}/>
            </button>

            {applySuccess ? (
              <div className="text-center py-4" data-testid="apply-modal-success">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-mustard border-2 border-black flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-black" strokeWidth={1.5}/>
                </div>
                <p className="qld-overline mb-3">application sent</p>
                <h3 className="font-serif text-3xl text-ink mb-3 leading-tight">
                  You're in the running.
                </h3>
                <p className="text-black/80 text-sm mb-7 max-w-sm mx-auto">
                  {applyOpp.vendor_name} will reach out directly if it's a fit. No middleman, no waiting room.
                </p>
                <button onClick={closeApply} className="qld-btn-primary" data-testid="apply-modal-done">
                  Got it
                </button>
              </div>
            ) : (
              <>
                <p className="qld-overline mb-3">{OPPORTUNITY_TYPES.find((t)=>t.value===applyOpp.type)?.label || applyOpp.type}</p>
                <h3 className="font-serif text-3xl text-ink mb-2 leading-tight">
                  {applyOpp.title}
                </h3>
                <p className="text-sm text-black/70 mb-6">
                  by {applyOpp.vendor_name}
                </p>

                <label className="block text-sm font-medium text-ink mb-1.5">
                  Drop a quick note <span className="text-black/50">(optional)</span>
                </label>
                <p className="text-xs text-black/60 mb-2">
                  Tell {applyOpp.vendor_name} why you'd be a great fit, what you're excited about, or anything they should know.
                </p>
                <textarea
                  rows={5}
                  className="qld-input w-full resize-none"
                  placeholder="e.g. We loved your last editorial — we'd be free that weekend and would bring our own florals."
                  value={applyNote}
                  onChange={(e)=>setApplyNote(e.target.value)}
                  data-testid="apply-modal-note"
                  disabled={applyBusy}
                  autoFocus
                />

                {applyError && (
                  <p className="text-sm text-wine mt-3" data-testid="apply-modal-error">{applyError}</p>
                )}

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <button
                    onClick={closeApply}
                    disabled={applyBusy}
                    className="lif-btn lif-btn-cream sm:order-1"
                    data-testid="apply-modal-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitApply}
                    disabled={applyBusy}
                    className="qld-btn-primary flex-1 sm:order-2"
                    data-testid="apply-modal-submit"
                  >
                    {applyBusy ? "Sending…" : <>Send application <Send className="w-4 h-4" strokeWidth={1.5}/></>}
                  </button>
                </div>

                <p className="text-xs text-black/60 mt-5 text-center">
                  {applyOpp.vendor_name} will see your profile and contact details — they'll reach out directly if it's a match.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
